import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { FileField } from "@/pages/DriverSignup";

export default function DriverReapply() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null); // {name, email, dot_number, rejection_reason}
  const [invalid, setInvalid] = useState(""); // message when link is dead
  const [loading, setLoading] = useState(true);
  const [dot, setDot] = useState("");
  const [license, setLicense] = useState(null);
  const [insurance, setInsurance] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setInvalid("This resubmission link is missing its token. Open the full link from your decision email.");
      setLoading(false);
      return;
    }
    api
      .get(`/driver-reapply/${token}`)
      .then((r) => {
        setInfo(r.data);
        setDot(r.data.dot_number || "");
      })
      .catch((err) => setInvalid(apiErrorMessage(err, "This resubmission link is not valid.")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!license || !insurance) {
      toast.error("Both documents are required — license and proof of insurance.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("dot_number", dot.trim());
      fd.append("license", license);
      fd.append("insurance", insurance);
      await api.post(`/driver-reapply/${token}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Resubmission failed — please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4 py-12" data-testid="reapply-page">
      <div className="w-full max-w-lg">
        <Link to="/" className="block text-center font-display text-2xl font-black uppercase tracking-tight mb-8">
          Boat<span className="text-[#FF3B30]">RV</span>Moving
        </Link>

        {loading ? (
          <div className="border border-white/[0.08] bg-[#0F0F10] p-10 text-center text-zinc-500 text-xs uppercase tracking-widest animate-pulse">
            Checking your link…
          </div>
        ) : invalid ? (
          <div className="border border-red-500/40 bg-[#0F0F10] p-10 text-center" data-testid="reapply-invalid">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="font-display text-3xl font-black uppercase">Link unavailable</h1>
            <p className="text-zinc-400 text-sm mt-4 leading-relaxed">{invalid}</p>
            <Link to="/" className="inline-block mt-8 text-[11px] uppercase tracking-widest font-bold text-[#FF3B30] hover:text-white transition-colors">
              ← Back to home
            </Link>
          </div>
        ) : done ? (
          <div className="border border-emerald-500/40 bg-[#0F0F10] p-10 text-center" data-testid="reapply-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h1 className="font-display text-3xl font-black uppercase">Back in review</h1>
            <p className="text-zinc-400 text-sm mt-4 leading-relaxed">
              Your fresh documents are in. A real human reviews every application — you'll get an email
              the moment a decision is made.
            </p>
            <p className="text-zinc-600 text-xs mt-6 uppercase tracking-widest">
              Your documents are securely stored and will never be shared.
            </p>
            <Link to="/" className="inline-block mt-8 text-[11px] uppercase tracking-widest font-bold text-[#FF3B30] hover:text-white transition-colors">
              ← Back to home
            </Link>
          </div>
        ) : (
          <div className="border border-white/[0.08] bg-[#0F0F10] p-8 md:p-10">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] font-bold">Resubmission</span>
            <h1 className="font-display text-3xl md:text-4xl font-black uppercase mt-2 leading-none">
              Welcome back, {info.name?.split(" ")[0]}
            </h1>
            <p className="text-sm text-zinc-500 mt-3">
              Resubmit against your existing account ({info.email}) — no need to register again.
            </p>

            {info.rejection_reason && (
              <div className="mt-5 border-l-2 border-[#FF3B30] bg-white/[0.03] p-4" data-testid="reapply-reason">
                <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1">Why it was declined</p>
                <p className="text-sm text-zinc-300 leading-relaxed">{info.rejection_reason}</p>
              </div>
            )}

            <div className="mt-4 flex items-start gap-3 border border-emerald-500/30 bg-emerald-500/[0.06] p-4" data-testid="reapply-security-note">
              <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-xs text-emerald-200/90 leading-relaxed">
                Your documents are securely stored and will never be shared.
              </p>
            </div>

            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="reapply-form" noValidate>
              <div>
                <label className="block text-[11px] uppercase tracking-widest font-bold text-zinc-400 mb-2">USDOT number</label>
                <input inputMode="numeric" value={dot}
                  onChange={(e) => setDot(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  placeholder="e.g. 3847592" data-testid="reapply-dot"
                  className="w-full bg-[#141416] border border-white/[0.08] focus:border-[#FF3B30] focus:outline-none text-white placeholder-zinc-600 p-4" />
                <p className="text-[11px] text-zinc-600 mt-1.5">Prefilled from your application — only change it if the number was the problem. Re-checked live against FMCSA SAFER.</p>
              </div>

              <FileField label="Driver's license" hint="Upload fresh license — JPG, PNG or PDF (max 5 MB)" file={license} onPick={setLicense} testid="reapply-license" />
              <FileField label="Proof of insurance" hint="Upload fresh insurance certificate — JPG, PNG or PDF (max 5 MB)" file={insurance} onPick={setInsurance} testid="reapply-insurance" />

              <button
                type="submit"
                disabled={submitting}
                data-testid="reapply-submit"
                className="w-full bg-[#FF3B30] hover:bg-[#ff5044] disabled:opacity-60 text-white font-bold text-sm uppercase tracking-widest py-4 transition-all flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Uploading…
                  </>
                ) : (
                  "Resubmit for review"
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
